import { useState, useRef } from 'react';
import { useOutsideAlerter } from '../../useOutsideAlerter';
import CreateStarter from './CreateStarter';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import useAnalytics from '../../analytics/analytics';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from '../../user/UserContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Pencil, MessageSquareText, Sparkles, MessageSquareDashed, Lock } from 'lucide-react';

const Starters = ({
    starters,
    setStarters,
    setNeedsUpdate,
    agId,
    agentName,
    enableFollowUps,
    setEnableFollowUps,
    followUpInstructions,
    setFollowUpInstructions,
    contextualStartersEnabled,
    setContextualStartersEnabled
}: {
    starters: any,
    setStarters: any,
    setNeedsUpdate: any,
    agId: string | undefined,
    agentName: string | undefined,
    enableFollowUps: boolean,
    setEnableFollowUps: any,
    followUpInstructions: string,
    setFollowUpInstructions: any,
    contextualStartersEnabled: boolean,
    setContextualStartersEnabled: any
}) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [activestarter, setActivestarter] = useState<any>(null);
    const { captureEvent } = useAnalytics();
    const { checkLicenseType } = useUser();

    const toggleModal = (starter?: string) => {
        if (modalVisible) {
            setActivestarter(null);
        } else {
            setActivestarter(starter);
        }
        setModalVisible(!modalVisible);
    };

    const addStarter = (starterValue: string) => {
        if (starterValue === "") {
            toast.error('starter name is required', toastConfig);
            return false;
        }

        if (starters.length >= 3) {
            toast.error('You can only have 3 starters', toastConfig);
            return false;
        }
        const starter = {
            id: uuidv4(),
            value: starterValue,
        };
        setStarters((prevState: any) => [...prevState, starter]);
        setNeedsUpdate(true);
        captureEvent('agent_starter_added', {
            agent_id: agId,
            agent_name: agentName,
        });
        return true;
    };

    const editStarter = (id: string, starterValue: string) => {
        let updatedStarters = starters.filter((starter: any) => starter.id !== id);

        updatedStarters = [...updatedStarters, { id: id, value: starterValue }];

        setStarters(updatedStarters);
        setNeedsUpdate(true);
    };

    const deletestarter = (id: string) => {
        setStarters(starters.filter((starter: any) => starter.id !== id));
        setNeedsUpdate(true);
        captureEvent('agent_starter_deleted', {
            agent_id: agId,
            agent_name: agentName,
        });
    };

    const modalRef = useRef(null);

    useOutsideAlerter(modalRef, () => toggleModal());

    return (
        <div className="space-y-3">
            {/* Starter List */}
            {starters && starters.length > 0 && (
                <div className="overflow-hidden rounded-md border bg-background divide-y">
                    {starters.map((starter: any) => (
                        <div
                            key={starter.id}
                            className="group flex min-h-11 items-center gap-2.5 px-3 py-2 transition-colors hover:bg-muted/30"
                        >
                            <MessageSquareText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <p
                                className="flex-1 text-sm cursor-pointer truncate"
                                onClick={() => toggleModal(starter)}
                            >
                                {starter.value}
                            </p>
                            <div className="flex items-center gap-1 shrink-0">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                    onClick={() => toggleModal(starter)}
                                    aria-label="Edit conversation starter"
                                >
                                    <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                    onClick={() => deletestarter(starter.id)}
                                    aria-label="Delete conversation starter"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Starter Button */}
            {starters.length < 3 && (
                <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-muted-foreground">{starters.length} of 3 starters</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => toggleModal()}>
                        <Plus className="mr-1.5 h-3.5 w-3.5" />
                        Add starter
                    </Button>
                </div>
            )}

            {starters.length >= 3 && (
                <p className="text-right text-xs text-muted-foreground">
                    Maximum of 3 starters reached.
                </p>
            )}

            <div className="overflow-hidden rounded-md border bg-background divide-y">
                {/* Contextual Starters Toggle */}
                <div className="flex items-center gap-3 p-3">
                    <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-medium">Contextual starters</h4>
                        <p className="text-xs leading-5 text-muted-foreground">
                            Generate suggestions from the page the visitor is viewing.
                        </p>
                    </div>
                    <Switch
                        checked={contextualStartersEnabled}
                        onCheckedChange={setContextualStartersEnabled}
                        className="shrink-0"
                    />
                </div>

                {/* AI Follow-ups Toggle */}
                <div className="p-3">
                    <div className="flex items-center gap-3">
                        <MessageSquareDashed className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <h4 className="text-sm font-medium">AI follow-ups</h4>
                                {!checkLicenseType('pro') && (
                                    <Badge variant="outline" className="text-[10px] text-muted-foreground">Engage</Badge>
                                )}
                            </div>
                            <p className="text-xs leading-5 text-muted-foreground">
                                Suggest next steps based on the conversation.
                            </p>
                        </div>
                        {checkLicenseType('pro') ? (
                            <Switch
                                checked={enableFollowUps}
                                onCheckedChange={setEnableFollowUps}
                                className="shrink-0"
                            />
                        ) : (
                            <Lock className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                        )}
                    </div>
                    {enableFollowUps && (
                        <div className="mt-3 space-y-1.5 border-t pt-3">
                            <label className="text-xs font-medium">Instructions</label>
                            <Textarea
                                placeholder="Generate follow-ups based on the conversation to convert the visitor into a paying customer."
                                value={followUpInstructions || ''}
                                onChange={(e) => setFollowUpInstructions(e.target.value)}
                                maxLength={200}
                                className="resize-none text-sm"
                                rows={3}
                            />
                        </div>
                    )}
                </div>
            </div>

            {modalVisible && (
                <CreateStarter
                    addStarter={addStarter}
                    editStarter={editStarter}
                    setModalVisible={setModalVisible}
                    starter={activestarter}
                />
            )}
        </div>
    );
};

export default Starters;
